import { Box, Button, Checkbox, Drawer, FormControlLabel, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import { IconSearch, IconX } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { CheckboxMultiple } from '~/components/Form/CheckboxMultiple'
import { useUsers } from '~/contexts'
import { EUserStatus } from '~/graphql/types'
import { SearchQueysData, useQueryString } from '~/hooks'

type FilterData = {
  status?: string[]
  accessControlId?: string[]
}

type FilterUsersDrawerProps = {
  drawerIsOpen: boolean
  setDrawerIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  toggleDrawer: () => void
  queryKeys: SearchQueysData[]
}

const userStatusOptions = [
  { value: EUserStatus.active, label: 'Ativo' },
  { value: EUserStatus.inactive, label: 'Inativo' },
]

export const FilterUsersDrawer: React.FC<FilterUsersDrawerProps> = ({ drawerIsOpen, toggleDrawer, setDrawerIsOpen, queryKeys }) => {
  const formMethods = useForm<FilterData>()
  const { searchQuerys, setSearchParams } = useQueryString()
  const queryValues = searchQuerys(queryKeys)
  const { accessControls } = useUsers()
  const [profileSearch, setProfileSearch] = useState('')

  const filteredProfiles = accessControls.filter(ac =>
    ac.name.toLowerCase().includes(profileSearch.toLowerCase())
  )

  useEffect(() => {
    if (drawerIsOpen) {
      formMethods.setValue('status', queryValues.status)
      formMethods.setValue('accessControlId', queryValues.accessControlId)
    }
  }, [drawerIsOpen])

  const handleFilters = (filters: FilterData) => {
    setDrawerIsOpen(false)
    setSearchParams(state => {
      state.set('page', '0')
      if (filters?.status && filters.status.length > 0) {
        state.set('status', JSON.stringify(filters.status))
      } else {
        state.delete('status')
      }
      if (filters?.accessControlId && filters.accessControlId.length > 0) {
        state.set('accessControlId', JSON.stringify(filters.accessControlId))
      } else {
        state.delete('accessControlId')
      }
      return state
    })
  }

  const onRemoveFilters = () => {
    setDrawerIsOpen(false)
    setSearchParams(state => {
      state.set('page', '0')
      state.delete('status')
      state.delete('accessControlId')
      return state
    })
  }

  return (
    <Drawer PaperProps={{ sx: { maxWidth: '560px', width: '100%', padding: '2rem', display: 'flex', flexDirection: 'column' } }} anchor='right' open={drawerIsOpen} onClose={toggleDrawer}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={500} variant='h5'>Filtrar por</Typography>
        <IconButton type='button' onClick={() => toggleDrawer()}>
          <IconX />
        </IconButton>
      </Box>

      <FormProvider {...formMethods}>
        <form style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }} onSubmit={formMethods.handleSubmit((formData) => handleFilters({ ...formData }))}>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Box sx={{ paddingTop: '1rem' }}>
              <Typography fontWeight={500} sx={{ paddingBottom: '.5rem' }}>Status</Typography>
              <CheckboxMultiple name='status' options={userStatusOptions} />
            </Box>

            <Box sx={{ paddingTop: '1.5rem' }}>
              <Typography fontWeight={500} sx={{ paddingBottom: '.5rem' }}>Perfil</Typography>
              <TextField
                fullWidth
                size='small'
                placeholder='Buscar perfil'
                value={profileSearch}
                onChange={(e) => setProfileSearch(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconSearch size={18} />
                    </InputAdornment>
                  )
                }}
              />
              <Controller
                name='accessControlId'
                control={formMethods.control}
                defaultValue={[]}
                render={({ field: { value = [], onChange } }) => (
                  <Box sx={{ display: 'flex', flexFlow: 'column', padding: '.8rem 0 0 .6rem' }}>
                    {filteredProfiles.map((ac) => (
                      <FormControlLabel
                        key={ac._id}
                        label={ac.name}
                        checked={value.includes(ac._id)}
                        onChange={() => {
                          if (value.includes(ac._id)) {
                            onChange(value.filter((id: string) => id !== ac._id))
                          } else {
                            onChange([...value, ac._id])
                          }
                        }}
                        control={<Checkbox />}
                      />
                    ))}
                    {filteredProfiles.length === 0 && (
                      <Typography variant='body2' color='text.secondary' sx={{ padding: '.5rem 0' }}>
                        Nenhum perfil encontrado
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', paddingTop: '2rem', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button onClick={() => onRemoveFilters()} type='button' fullWidth color='secondary'>Limpar</Button>
            <Button type='submit' fullWidth color='primary'>Aplicar</Button>
          </Box>
        </form>
      </FormProvider>
    </Drawer>
  )
}
